/**
 * ApproverDelegationPanel — list + grant + revoke time-bounded approval
 * re-routes. Spec 62 D13 Task 36.
 *
 * Active delegations the user is party to (granted OR received) appear in the
 * list. Granting requires picking a delegate, an approval pattern, and a TTL.
 * Revoke fires inline; the server enforces "must be a party" beyond the policy
 * gate.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, UserCheck } from 'lucide-react';
import { z } from 'zod';
import { Badge, Button, Card, EmptyState, QueryBoundary } from '../ui';
import { Can } from '../auth/Can.js';
import { Form, FormActions, NumberFormField, SelectFormField, TextFormField, useZodForm } from '../form';
import {
  createApproverDelegationMutation,
  listApproverDelegationsOptions,
  listApproverDelegationsQueryKey,
  revokeApproverDelegationMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { ApproverDelegation } from '../../generated/api/types.gen.js';
import { zApproverDelegationCreate } from '../../generated/api/zod.gen.js';
import { useAuthStore } from '../../store';

const DURATIONS = [
  { label: '4 hours', hours: 4 },
  { label: '12 hours', hours: 12 },
  { label: '24 hours', hours: 24 },
  { label: '3 days', hours: 72 },
  { label: '1 week', hours: 168 },
].map(({ label, hours }) => ({ value: hours, label }));

const APPROVAL_PREFIX = 'approval:requested:';

export function ApproverDelegationPanel() {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => {
    const id = s.user?.id;
    if (id === undefined || id === null) return null;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  });
  const [showGrantForm, setShowGrantForm] = useState(false);

  const delegationsQuery = useQuery(listApproverDelegationsOptions({ query: { activeOnly: true } }));

  const revoke = useMutation({
    ...revokeApproverDelegationMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listApproverDelegationsQueryKey() }),
  });

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-heading">Approval delegations</h3>
          <p className="text-xs text-muted">Re-route approval events to another user for a bounded window.</p>
        </div>
        {!showGrantForm && (
          <Can policy="CanManageApproverDelegations">
            <Button onClick={() => setShowGrantForm(true)}>
              <Plus size={14} /> Grant
            </Button>
          </Can>
        )}
      </div>

      {showGrantForm && (
        <GrantForm
          onClose={() => setShowGrantForm(false)}
          onCreated={() => {
            setShowGrantForm(false);
            queryClient.invalidateQueries({ queryKey: listApproverDelegationsQueryKey() });
          }}
        />
      )}

      <QueryBoundary
        query={delegationsQuery}
        label="approval delegations"
        compact
        loading={<div className="text-sm text-muted">Loading delegations…</div>}
      >
        {(list) =>
          list.length === 0 ? (
            <EmptyState
              icon={UserCheck}
              title="No active delegations"
              description="Grant a delegation when you want a peer to handle a slice of approvals while you're away."
            />
          ) : (
            <ul className="space-y-2">
              {list.map((d) => (
                <li key={d.id}>
                  <DelegationRow
                    delegation={d}
                    youAreId={userId}
                    onRevoke={() => revoke.mutate({ path: { id: String(d.id) } })}
                    revoking={revoke.isPending}
                  />
                </li>
              ))}
            </ul>
          )
        }
      </QueryBoundary>

      {revoke.isError && (
        <p className="text-sm text-danger">{(revoke.error as Error)?.message ?? 'Could not revoke delegation'}</p>
      )}
    </Card>
  );
}

function DelegationRow({
  delegation,
  youAreId,
  onRevoke,
  revoking,
}: {
  delegation: ApproverDelegation;
  youAreId: number | null;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const youAreDelegator = youAreId === delegation.delegatorUserId;
  const youAreDelegate = youAreId === delegation.delegateUserId;
  const expiresIn = formatExpiresIn(delegation.expiresAt);

  return (
    <Card className="p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <code className="text-xs bg-surface-alt px-1">{delegation.approvalPattern}</code>
            {youAreDelegator && <Badge variant="info">You delegated</Badge>}
            {youAreDelegate && <Badge variant="success">You receive</Badge>}
          </div>
          <div className="text-sm text-muted">
            <span className="text-body">User #{delegation.delegatorUserId}</span>
            {' → '}
            <span className="text-body">User #{delegation.delegateUserId}</span>
            <span className="ml-2">· expires in {expiresIn}</span>
          </div>
          <div className="text-xs text-faint mt-0.5">granted {new Date(delegation.grantedAt).toLocaleString()}</div>
        </div>
        {(youAreDelegator || youAreDelegate) && (
          <Can policy="CanManageApproverDelegations">
            <Button variant="ghost" iconOnly onClick={onRevoke} disabled={revoking} aria-label="Revoke delegation">
              <Trash2 size={14} aria-hidden="true" />
            </Button>
          </Can>
        )}
      </div>
    </Card>
  );
}

/** The generated request schema plus the two UI-only fields the request is built from. */
const grantSchema = zApproverDelegationCreate.pick({ delegateUserId: true }).extend({
  patternTail: z.string().trim().min(1),
  hours: z.number(),
});

function GrantForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const create = useMutation(createApproverDelegationMutation());
  const form = useZodForm(grantSchema, { defaultValues: { delegateUserId: undefined, patternTail: '*', hours: 24 } });

  const submit = async ({ delegateUserId, patternTail, hours }: z.output<typeof grantSchema>) => {
    await create.mutateAsync({
      body: {
        delegateUserId,
        approvalPattern: `${APPROVAL_PREFIX}${patternTail}`,
        expiresAt: new Date(Date.now() + hours * 60 * 60 * 1000).toISOString(),
      },
    });
    onCreated();
  };

  return (
    <Form form={form} onSubmit={submit} className="rounded-sm bg-surface-alt p-3">
      <NumberFormField
        name="delegateUserId"
        label="Delegate to"
        description="The user's number."
        minValue={1}
        required
      />
      <TextFormField
        name="patternTail"
        label="Approvals to hand over"
        description={`Matches ${APPROVAL_PREFIX} followed by this text, for example * or order:place.`}
        required
      />
      <SelectFormField name="hours" label="Duration" options={DURATIONS} required />
      <FormActions submitLabel="Grant" onCancel={onClose} />
    </Form>
  );
}

function formatExpiresIn(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours < 1) {
    const minutes = Math.max(1, Math.floor(ms / 60_000));
    return `${minutes}m`;
  }
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}
